import { useEffect, useState } from "react";
import { Plus, Hash, X, AlertTriangle, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SALARY_TAB } from "@/constants/testIds";
import { toast } from "sonner";
import { IsrEstimateCard } from "./IsrEstimateCard";

const PERIODICITY_OPTIONS = [
  { value: "mensual", label: "Mensual" },
  { value: "quincenal", label: "Quincenal" },
  { value: "semanal", label: "Semanal" },
  { value: "anual", label: "Anual" },
  { value: "único", label: "Único" },
];

const PAY_PERIODICITY_OPTIONS = [
  { value: "mensual", label: "Mensual" },
  { value: "quincenal", label: "Quincenal" },
  { value: "semanal", label: "Semanal" },
];

const VISIBILITY_OPTIONS = [
  { value: "hidden", label: "Oculto por completo" },
  { value: "total_only", label: "Solo total consolidado" },
  { value: "full", label: "Desglose completo" },
];

const emptyComponent = () => ({ name: "", amount: "", periodicity: "mensual", tag: "variable", isr_taxable: true });

export function SalaryTab({ employeeId, currentSalary, history, visibility, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ base_salary: "", effective_date: "", reason: "", periodicidad_pago: "mensual" });
  const [components, setComponents] = useState([]);
  const [savingVisibility, setSavingVisibility] = useState(false);
  const [isrPreview, setIsrPreview] = useState(null);
  const [isrLoading, setIsrLoading] = useState(false);

  const base = parseFloat(form.base_salary) || 0;
  const componentsTotal = components.reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
  const previewTotal = base + componentsTotal;
  const sdiRisk = components.some((c) => c.tag === "fijo_recurrente" && parseFloat(c.amount) >= 0.3 * base && base > 0);

  const addComponent = () => setComponents((prev) => [...prev, emptyComponent()]);
  const updateComponent = (idx, field, value) => setComponents((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  const removeComponent = (idx) => setComponents((prev) => prev.filter((_, i) => i !== idx));

  useEffect(() => {
    if (!open || base <= 0) {
      setIsrPreview(null);
      return;
    }
    const timer = setTimeout(async () => {
      setIsrLoading(true);
      try {
        const { data } = await api.post(`/employees/${employeeId}/compensation/isr-preview`, {
          base_salary: base,
          periodicidad_pago: form.periodicidad_pago,
          components: components
            .filter((c) => c.name && c.amount)
            .map((c) => ({ name: c.name, amount: parseFloat(c.amount), periodicity: c.periodicity, tag: c.tag, isr_taxable: c.isr_taxable !== false })),
        });
        setIsrPreview(data);
      } catch (err) {
        setIsrPreview(null);
      } finally {
        setIsrLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, base, form.periodicidad_pago, JSON.stringify(components)]);

  const handleSubmit = async () => {
    try {
      const { data } = await api.post(`/employees/${employeeId}/compensation`, {
        base_salary: base,
        effective_date: form.effective_date,
        reason: form.reason || null,
        periodicidad_pago: form.periodicidad_pago,
        components: components
          .filter((c) => c.name && c.amount)
          .map((c) => ({ name: c.name, amount: parseFloat(c.amount), periodicity: c.periodicity, tag: c.tag, isr_taxable: c.isr_taxable !== false })),
      });
      toast.success(data.message);
      if (data.sdi_warnings?.length) {
        data.sdi_warnings.forEach((w) => toast.warning(w, { duration: 8000 }));
      }
      if (data.isr_estimate?.alert_net_not_improving) {
        toast.warning("El neto estimado no mejora a pesar del aumento en el bruto. Revisa el detalle en el historial.", { duration: 8000 });
      }
      setOpen(false);
      setForm({ base_salary: "", effective_date: "", reason: "", periodicidad_pago: "mensual" });
      setComponents([]);
      setIsrPreview(null);
      onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleVisibilityChange = async (value) => {
    setSavingVisibility(true);
    try {
      await api.patch(`/employees/${employeeId}/compensation-visibility`, { visibility: value });
      toast.success("Visibilidad actualizada");
      onUpdated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingVisibility(false);
    }
  };

  return (
    <div className="space-y-3 mt-4">
      <div className="bg-card border border-petroleum/10 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs text-charcoal/50">Compensación total actual</p>
          <p data-testid={SALARY_TAB.currentSalary} className="text-2xl font-heading font-bold text-charcoal">
            {currentSalary != null ? `$${currentSalary.toLocaleString("es-MX")}` : "Sin registrar"}
          </p>
          {currentSalary == null && (
            <p data-testid={SALARY_TAB.checkpointRequiresSalaryNotice} className="text-xs text-amber-700 flex items-center gap-1 mt-1">
              <Info className="w-3 h-3 shrink-0" /> Registra el salario para poder usar la pestaña "Considera Antes de Decidir".
            </p>
          )}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={SALARY_TAB.newEntryButton} size="sm" className="bg-terracotta hover:bg-terracotta/90 rounded-full">
              <Plus className="w-4 h-4 mr-1" /> Registrar cambio de compensación
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-bone max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Nueva compensación</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Salario base mensual (MXN)</Label>
                <Input data-testid={SALARY_TAB.salaryInput} type="number" min="1" value={form.base_salary} onChange={(e) => setForm((f) => ({ ...f, base_salary: e.target.value }))} />
              </div>
              <div>
                <Label>Fecha efectiva</Label>
                <Input data-testid={SALARY_TAB.effectiveDateInput} type="date" value={form.effective_date} onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))} />
              </div>
              <div>
                <Label>Periodicidad de pago (para el estimado de ISR)</Label>
                <Select value={form.periodicidad_pago} onValueChange={(v) => setForm((f) => ({ ...f, periodicidad_pago: v }))}>
                  <SelectTrigger data-testid={SALARY_TAB.periodicidadPagoSelect}><SelectValue /></SelectTrigger>
                  <SelectContent>{PAY_PERIODICITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Motivo (opcional)</Label>
                <Input data-testid={SALARY_TAB.reasonInput} value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="Ej. Aumento anual, cambio de puesto" />
              </div>

              <div className="pt-1">
                <div className="flex items-center justify-between mb-2">
                  <Label>Componentes adicionales (opcional)</Label>
                  <Button data-testid={SALARY_TAB.addComponentButton} type="button" size="sm" variant="outline" onClick={addComponent} className="rounded-full h-7 text-xs">
                    <Plus className="w-3 h-3 mr-1" /> Agregar
                  </Button>
                </div>
                <div className="space-y-2">
                  {components.map((c, idx) => (
                    <div key={idx} className="bg-white border border-petroleum/10 rounded-lg p-3 grid grid-cols-2 gap-2 relative">
                      <button type="button" onClick={() => removeComponent(idx)} data-testid={`${SALARY_TAB.componentRemoveButton}-${idx}`} className="absolute top-2 right-2 text-charcoal/40 hover:text-red-600">
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <Input data-testid={`${SALARY_TAB.componentNameInput}-${idx}`} placeholder="Nombre (ej. Bono, Comisión)" value={c.name} onChange={(e) => updateComponent(idx, "name", e.target.value)} className="col-span-2 text-sm" />
                      <Input data-testid={`${SALARY_TAB.componentAmountInput}-${idx}`} type="number" placeholder="Monto" value={c.amount} onChange={(e) => updateComponent(idx, "amount", e.target.value)} className="text-sm" />
                      <Select value={c.periodicity} onValueChange={(v) => updateComponent(idx, "periodicity", v)}>
                        <SelectTrigger data-testid={`${SALARY_TAB.componentPeriodicitySelect}-${idx}`} className="text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>{PERIODICITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={c.tag} onValueChange={(v) => updateComponent(idx, "tag", v)}>
                        <SelectTrigger data-testid={`${SALARY_TAB.componentTagSelect}-${idx}`} className="text-sm col-span-2"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fijo_recurrente">Fijo / recurrente</SelectItem>
                          <SelectItem value="variable">Variable</SelectItem>
                        </SelectContent>
                      </Select>
                      <label className="col-span-2 flex items-center gap-2 text-xs text-charcoal/70 pt-1">
                        <Checkbox
                          data-testid={`${SALARY_TAB.componentIsrTaxableCheckbox}-${idx}`}
                          checked={c.isr_taxable !== false}
                          onCheckedChange={(v) => updateComponent(idx, "isr_taxable", v === true)}
                        />
                        Gravable para ISR (incluir en el estimado de impuesto)
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div data-testid={SALARY_TAB.totalPreview} className="bg-petroleum/5 border border-petroleum/15 rounded-lg p-3 flex justify-between items-center">
                <span className="text-sm text-charcoal/70">Total mensual estimado</span>
                <span className="font-heading font-bold text-charcoal">${previewTotal.toLocaleString("es-MX")}</span>
              </div>

              {sdiRisk && (
                <div data-testid={SALARY_TAB.sdiWarning} className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">Un componente fijo/recurrente es grande respecto al salario base: probablemente integra al SDI (Salario Diario Integrado). Valida el cálculo con tu contador antes de aplicar cambios en el IMSS.</p>
                </div>
              )}

              {isrLoading && <p className="text-xs text-charcoal/40">Calculando estimado de ISR...</p>}
              <IsrEstimateCard estimate={isrPreview} testId={SALARY_TAB.isrPreviewCard} />

              <p className="text-xs text-charcoal/50">Este registro es inmutable: quedará en el historial junto con la compensación anterior, la fecha y quién lo autorizó.</p>
              <Button data-testid={SALARY_TAB.submitButton} disabled={!form.base_salary || !form.effective_date} onClick={handleSubmit} className="w-full bg-petroleum rounded-full">Guardar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-petroleum/10 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm font-medium text-charcoal">Visibilidad para el empleado</p>
          <p className="text-xs text-charcoal/50">Solo tú decides qué ve el empleado. Nunca sabrá cuál modo está activo.</p>
        </div>
        <Select value={visibility} onValueChange={handleVisibilityChange} disabled={savingVisibility}>
          <SelectTrigger data-testid={SALARY_TAB.visibilitySelect} className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>{VISIBILITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <p className="text-sm font-semibold text-charcoal pt-2">Historial de compensación (inmutable)</p>
      {history.map((c) => (
        <div key={c.id} data-testid={SALARY_TAB.historyRow} className="bg-card border border-petroleum/10 rounded-xl p-4">
          <div className="flex justify-between">
            <p className="text-charcoal">{c.previous_salary != null ? `$${c.previous_salary.toLocaleString("es-MX")} → ` : ""}<span className="font-semibold">{c.monthly_salary != null ? `$${c.monthly_salary.toLocaleString("es-MX")}` : "Sin registrar"}</span></p>
            <span className="text-xs text-charcoal/50 whitespace-nowrap ml-3">{c.effective_date || "—"}</span>
          </div>
          <p className="text-xs text-charcoal/60 mt-1">Base: {c.base_salary != null ? `$${c.base_salary.toLocaleString("es-MX")}` : "Sin registrar"}{Array.isArray(c.components) && c.components.length > 0 && ` + ${c.components.length} componente(s)`}</p>
          {Array.isArray(c.components) && c.components.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {c.components.map((comp, i) => (
                <span key={i} className={`text-xs px-2 py-0.5 rounded-full border ${comp.tag === "fijo_recurrente" ? "border-petroleum/30 text-petroleum bg-petroleum/5" : "border-terracotta/30 text-terracotta bg-terracotta/5"}`}>
                  {comp.name}: {comp.amount != null ? `$${comp.amount.toLocaleString("es-MX")}` : "—"} ({comp.periodicity})
                </span>
              ))}
            </div>
          )}
          {c.reason && <p className="text-sm text-charcoal/60 mt-1">{c.reason}</p>}
          {c.isr_estimate && (
            <div className="mt-3">
              <IsrEstimateCard estimate={c.isr_estimate} testId={SALARY_TAB.isrHistoryCard} />
            </div>
          )}
          <p className="text-xs text-charcoal/40 mt-2 flex items-center gap-1 font-mono-legal"><Hash className="w-3 h-3" /> #{c.sequence ?? "—"} · {c.hash ? `${c.hash.slice(0, 12)}...` : "sin hash"} · autorizó {c.changed_by || "—"}</p>
        </div>
      ))}
      {history.length === 0 && <p className="text-charcoal/50 text-sm">Sin historial de compensación registrado.</p>}
    </div>
  );
}
